import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { getVatTypes } from 'gms/src/core/widgets/VatDropdown/actions'
import { IVatTypesResponse } from '@cbgms/api/modules/vat'
import { formatEnum } from '@cbgms/base/utils/helpers'
import { VatType, WorkorderLineType } from '@cbgms/api/modules/enums'
import { useQuery } from '@tanstack/react-query'

export interface vatFilter {
  specificVatTypes?: string[]
  onLineType?: {
    lineType: WorkorderLineType
    activityUUID?: string
    WarehousePartGroupUUID?: string
    WarehousePartUUID?: string
    LabourRateUUID?: string
    ActivityUUID?: string
    SundryUUID?: string
    CourtesyVehicleUUID?: string
    RentalVehicleUUID?: string
  }
  onRevenueGroup?: string
}

export interface vatFilterContextType {
  onLineType?: {
    lineType: WorkorderLineType.RentalVehicle | WorkorderLineType.CourtesyVehicle
    CourtesyVehicleUUID?: string
    RentalVehicleUUID?: string
  }
  onRevenueGroup?: string
  specificVatTypes?: string[]
}

export enum IAutoCorrectMode {
  disabled, // don't do anything for auto correction
  preferDefault, // select default provided by backend
  emptyWhenUnavailable, // set value as empty
  preferOriginalValue // try to select the original value when it's available
}

export type AutoCorrectFallback = 'first-result'

export interface GetVatTypesOptions {
  onChange?: (vatType: VatType | '') => void
  currentValue?: VatType
  initialValue?: VatType
  filter?: vatFilter
  showAll?: boolean
  autoCorrectMode?: IAutoCorrectMode // autoCorrect can automatically fix when the current value is not available in the options
  autoCorrectFallback?: AutoCorrectFallback
  multiple?: boolean
}

const useGetVatTypesOptions = ({
  onChange,
  currentValue,
  initialValue,
  filter,
  showAll,
  autoCorrectMode,
  autoCorrectFallback,
  multiple
}: Partial<GetVatTypesOptions> = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { data, isLoading } = useQuery(
    [
      filter?.onLineType?.lineType,
      filter?.onLineType?.CourtesyVehicleUUID,
      filter?.onLineType?.RentalVehicleUUID,
      filter?.onLineType?.ActivityUUID,
      filter?.onLineType?.LabourRateUUID,
      filter?.onLineType?.SundryUUID,
      filter?.onLineType?.WarehousePartGroupUUID,
      filter?.onLineType?.WarehousePartUUID,
      filter?.onRevenueGroup
    ],
    async () => {
      const response = await dispatch(getVatTypes({ revenueGroupUUID: filter?.onRevenueGroup, ...filter?.onLineType }))
      return response.Data as IVatTypesResponse
    }
  )

  const preferredDefault = useMemo(() => data?.VatTypes.find(a => !!a.PreferredDefault)?.Type || '', [data])
  const filteredVatTypes = useMemo(() => {
    const filtered = (data?.VatTypes || []).map(vat => ({
      key: vat.Type,
      value: vat.Type,
      text: `${formatEnum('EnumVatType', vat.Type)} (${vat.Percentage}%)`,
      percent: vat.Percentage,
      disabled: !!filter?.specificVatTypes?.length && !filter?.specificVatTypes.find(onFilterItem => vat.Type === onFilterItem)
    }))

    if (showAll && (filter?.specificVatTypes?.length || 0) < 1) {
      filtered.unshift({ value: 'all' as any, key: 'all' as any, text: t('global.all'), percent: 0, disabled: false })
    }
    return filtered
  }, [data, showAll])

  useEffect(() => {
    if (!multiple && autoCorrectMode !== IAutoCorrectMode.disabled && !isLoading && !filteredVatTypes.find(s => s.value === currentValue)) {
      // the value is not present anymore
      let newValue: VatType | '' = ''

      if (autoCorrectMode === IAutoCorrectMode.emptyWhenUnavailable) {
        newValue = filteredVatTypes.find(a => a.value === currentValue)?.value || ''
      } else if (autoCorrectMode === IAutoCorrectMode.preferOriginalValue) {
        newValue = filteredVatTypes.find(a => a.value === initialValue)?.value || preferredDefault
      } else if (autoCorrectMode === IAutoCorrectMode.preferDefault) {
        newValue = preferredDefault
      }

      if (newValue === '') {
        if (autoCorrectFallback === 'first-result' && filteredVatTypes.length > 0) {
          newValue = filteredVatTypes[0].value
        }
      }

      if (onChange) {
        onChange(newValue)
      }
    }
  }, [multiple, isLoading, preferredDefault, autoCorrectMode, filteredVatTypes, initialValue])

  return useMemo(
    () => ({
      options: filteredVatTypes,
      // not sure why, but for some reason typescript infers only this usage of preferredDefault as "string" instead of "VatType | '' ".
      preferredDefault: preferredDefault as VatType | '',
      isLoading
    }),
    [filteredVatTypes, preferredDefault, isLoading]
  )
}

export default useGetVatTypesOptions
